import React, { useState, useEffect } from 'react';
import { Check, X, PencilSimple } from '@phosphor-icons/react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface EditableSelectFieldProps {
  icon: React.ComponentType<any>;
  label: string;
  field: string;
  value: any;
  options: SelectOption[];
  isLoading?: boolean;
  isRequired?: boolean;
  onSave: (field: string, value: any) => void;
  isSaving?: boolean;
  showSuccess?: boolean;
  showStatusIcon?: boolean;
  statusIconType?: 'available' | 'yesno' | 'remote';
}

export const EditableSelectField: React.FC<EditableSelectFieldProps> = ({
  icon: Icon,
  label,
  field,
  value,
  options,
  isLoading = false,
  isRequired = false,
  onSave,
  isSaving = false,
  showSuccess = false,
  showStatusIcon = false,
  statusIconType = 'available'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  
  // Update local value when prop value changes (but not during editing)
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value || '');
      console.log(`📋 SelectField ${field} updated to:`, value);
    }
  }, [value, isEditing, field]);
  
  const startEditing = () => {
    setLocalValue(value || '');
    setIsEditing(true);
    console.log(`✏️ Started editing select ${field} with value:`, value);
  };
  
  const cancelEditing = () => {
    console.log(`❌ Cancelled editing select ${field}`);
    setLocalValue(value || '');
    setIsEditing(false);
  };
  
  const handleSave = () => {
    console.log(`💾 Saving select ${field} with value:`, localValue);
    onSave(field, localValue);
    setIsEditing(false);
  };
  
  const getStatusIcon = (value: any) => {
    let isPositive = false;
    
    if (statusIconType === 'available') {
      isPositive = value === 'Beschikbaar';
    } else if (statusIconType === 'yesno') {
      isPositive = value === 'Ja';
    } else if (statusIconType === 'remote') {
      isPositive = value === 'Videofoon' || value === 'Analoge toegang';
    }
    
    const isNegative = (statusIconType === 'available' && value === 'Niet beschikbaar') || 
                     (statusIconType === 'yesno' && value === 'Nee') ||
                     (statusIconType === 'remote' && value === 'Geen toegang op afstand mogelijk');
    
    if (isPositive) {
      return <Check size={16} weight="bold" className="text-status-good" />;
    } else if (isNegative) {
      return <X size={16} weight="bold" className="text-brick-primary" />;
    } else {
      return <div className="w-4 h-4 rounded-full bg-neutral-secondary-mid/30"></div>;
    }
  };
  
  if (!isEditing) {
    return (
      <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-neutral-primary/60" />
          <span className="text-sm font-medium text-neutral-primary">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {showStatusIcon && !isLoading && getStatusIcon(value)}
          <span className="text-sm text-neutral-primary min-w-[100px] text-right">
            {isLoading ? 'Laden...' : (value || 'Niet opgegeven')}
          </span>
          {showSuccess && (
            <div className="flex items-center gap-1 px-1 py-0.5 bg-status-good text-white rounded-sm text-xs">
              <Check size={10} weight="bold" />
            </div>
          )}
          <button
            onClick={startEditing}
            className="text-neutral-primary/40 hover:text-moss-primary transition-colors"
            disabled={isLoading}
          >
            <PencilSimple size={16} />
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-between p-2 border-b border-neutral-primary/10 last:border-b-0">
      <div className="flex items-center gap-2">
        <Icon size={18} className="text-neutral-primary/60" />
        <span className="text-sm font-medium text-neutral-primary">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <select
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          className="border border-neutral-primary/20 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:border-moss-primary min-w-[120px]"
          autoFocus
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={isSaving}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSave();
          }}
          className={`transition-colors ${
            isSaving 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-status-good hover:text-status-good/80'
          }`}
        >
          {isSaving ? (
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Check size={16} weight="bold" />
          )}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cancelEditing();
          }}
          className="text-brick-primary hover:text-brick-primary/80 transition-colors"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
};
